import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Heart, HeartHandshake, Sparkles } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * OnboardingTour - Guided tooltip tour for new users
 * 
 * Shows a 4-step tour explaining the main features:
 * 1. Welcome to your dashboard
 * 2. Regulation Hub explanation
 * 3. Relationships Hub explanation
 * 4. How to use tool cards
 * 
 * Tour completion is persisted both in localStorage (for quick access)
 * and in the user's database profile (for cross-device sync)
 */

const TOUR_STORAGE_KEY = 'coregulate_tour_completed';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tour step configurations
const TOUR_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to your dashboard',
        description: 'This is your home base for emotional regulation. Everything you need is organized into two simple hubs.',
        position: 'center',
        icon: Sparkles,
        highlight: null
    },
    {
        id: 'regulation',
        title: 'Regulation Hub',
        description: 'Tools for calming your nervous system when you\'re overwhelmed, anxious, or spiraling. Start here when you need to regulate yourself.',
        position: 'below',
        icon: Heart,
        highlight: '[data-testid="tab-regulation"]',
        variant: 'sage'
    },
    {
        id: 'relationships',
        title: 'Relationships Hub',
        description: 'Tools for regulating your relationships. Use these when navigating conflict, co-regulating with a partner, or when relationships feel hard.',
        position: 'below',
        icon: HeartHandshake,
        highlight: '[data-testid="tab-relationships"]',
        variant: 'rose'
    },
    {
        id: 'tools',
        title: 'Tap any card to start',
        description: 'Each card is a tool designed for ADHD brains. They\'re bite-sized and meet you where you are. Just tap to begin.',
        position: 'above',
        icon: ChevronRight,
        highlight: '[data-testid^="quick-action-"]:first-of-type',
        variant: 'lavender'
    }
];

export const OnboardingTour = ({ onComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [targetRect, setTargetRect] = useState(null);
    const [isVisible, setIsVisible] = useState(true);
    const { token } = useAuth();

    const step = TOUR_STEPS[currentStep];
    const isLastStep = currentStep === TOUR_STEPS.length - 1;
    const isFirstStep = currentStep === 0;

    // Find and highlight target element
    useEffect(() => {
        if (step.highlight) {
            const target = document.querySelector(step.highlight);
            if (target) {
                const rect = target.getBoundingClientRect();
                setTargetRect(rect);
                // Scroll element into view if needed
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
                setTargetRect(null);
            }
        } else {
            setTargetRect(null);
        }
    }, [currentStep, step.highlight]);

    // Save tour completion to both localStorage and database
    const saveTourCompletion = async () => {
        localStorage.setItem(TOUR_STORAGE_KEY, 'true');
        
        // Also save to user profile for cross-device sync
        if (token) {
            try {
                await fetch(`${API_URL}/api/users/me/preferences`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ tour_completed: true })
                });
            } catch (error) {
                console.error('Failed to save tour preference:', error);
            }
        }
    };

    const handleNext = () => {
        if (isLastStep) {
            handleComplete();
        } else {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (!isFirstStep) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleComplete = () => {
        saveTourCompletion();
        setIsVisible(false);
        onComplete?.();
    };

    const handleSkip = () => {
        saveTourCompletion();
        setIsVisible(false);
        onComplete?.();
    };

    if (!isVisible) return null;

    // Calculate tooltip position
    const getTooltipStyle = () => {
        if (!targetRect || step.position === 'center') {
            return {
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
            };
        }

        const padding = 16;
        const tooltipWidth = 320;

        if (step.position === 'below') {
            return {
                top: `${targetRect.bottom + padding}px`,
                left: `${Math.max(padding, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - padding))}px`,
                transform: 'none'
            };
        }

        if (step.position === 'above') {
            return {
                bottom: `${window.innerHeight - targetRect.top + padding}px`,
                left: `${Math.max(padding, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - padding))}px`,
                transform: 'none'
            };
        }

        return {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
        };
    };

    const Icon = step.icon;
    const variantColors = {
        sage: 'bg-[#7D8E95]/20 text-[#1D6B6B]',
        rose: 'bg-[#FFBB98]/30 text-[#FFBB98]',
        lavender: 'bg-[#7D8E95]/30 text-[#7D8E95]',
        default: 'bg-[#7D8E95]/20 text-[#1D6B6B]'
    };

    return createPortal(
        <div className="fixed inset-0 z-[100]" data-testid="onboarding-tour">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
                onClick={handleSkip}
            />
            
            {/* Highlight cutout */}
            {targetRect && (
                <div 
                    className="absolute rounded-2xl ring-4 ring-[#1D6B6B] ring-opacity-60 bg-transparent pointer-events-none transition-all duration-300"
                    style={{
                        top: targetRect.top - 8,
                        left: targetRect.left - 8,
                        width: targetRect.width + 16,
                        height: targetRect.height + 16,
                        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)'
                    }}
                />
            )}

            {/* Tooltip */}
            <div
                className="absolute w-80 bg-white rounded-2xl shadow-xl p-6 transition-all duration-300"
                style={getTooltipStyle()}
            >
                {/* Close button */}
                <button
                    onClick={handleSkip}
                    className="absolute top-3 right-3 p-1 text-[#7D8E95] hover:text-[#7D8E95] transition-colors"
                    aria-label="Close tour"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Icon */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${variantColors[step.variant] || variantColors.default}`}>
                    <Icon className="w-6 h-6" />
                </div>

                {/* Content */}
                <h3 className="text-lg font-semibold text-[#1D6B6B] mb-2 font-['Lora']">
                    {step.title}
                </h3>
                <p className="text-sm text-[#7D8E95] mb-6 leading-relaxed">
                    {step.description}
                </p>

                {/* Progress dots */}
                <div className="flex items-center justify-center gap-2 mb-4">
                    {TOUR_STEPS.map((_, index) => (
                        <div
                            key={index}
                            className={`w-2 h-2 rounded-full transition-all duration-200 ${
                                index === currentStep
                                    ? 'bg-[#1D6B6B] w-4'
                                    : index < currentStep
                                    ? 'bg-[#7D8E95]'
                                    : 'bg-[#FFBB98]'
                            }`}
                        />
                    ))}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between">
                    <button
                        onClick={handlePrev}
                        disabled={isFirstStep}
                        className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                            isFirstStep
                                ? 'text-[#D0D0CD] cursor-not-allowed'
                                : 'text-[#7D8E95] hover:text-[#1D6B6B]'
                        }`}
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back
                    </button>
                    
                    <button
                        onClick={handleNext}
                        className="flex items-center gap-1 px-4 py-2 bg-[#1D6B6B] text-white text-sm font-medium rounded-full hover:bg-[#1D6B6B] transition-colors"
                    >
                        {isLastStep ? 'Get started' : 'Next'}
                        {!isLastStep && <ChevronRight className="w-4 h-4" />}
                    </button>
                </div>

                {/* Skip link */}
                <button
                    onClick={handleSkip}
                    className="w-full text-center text-xs text-[#7D8E95] hover:text-[#7D8E95] mt-3 transition-colors"
                >
                    Skip tour
                </button>
            </div>
        </div>,
        document.body
    );
};

// Hook to check if tour should be shown
export const useTourStatus = () => {
    const [shouldShowTour, setShouldShowTour] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { token } = useAuth();

    useEffect(() => {
        const checkTourStatus = async () => {
            // First check localStorage (quick)
            const hasCompletedTourLocal = localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
            
            if (hasCompletedTourLocal) {
                setShouldShowTour(false);
                setIsLoading(false);
                return;
            }

            // If not in localStorage and user is logged in, check database
            if (token) {
                try {
                    const response = await fetch(`${API_URL}/api/users/me/preferences`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                        const prefs = await response.json();
                        if (prefs.tour_completed) {
                            // Sync to localStorage
                            localStorage.setItem(TOUR_STORAGE_KEY, 'true');
                            setShouldShowTour(false);
                            setIsLoading(false);
                            return;
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch tour preference:', error);
                }
            }

            // Tour not completed
            setShouldShowTour(true);
            setIsLoading(false);
        };

        checkTourStatus();
    }, [token]);

    const resetTour = () => {
        localStorage.removeItem(TOUR_STORAGE_KEY);
        setShouldShowTour(true);
    };

    const completeTour = () => {
        localStorage.setItem(TOUR_STORAGE_KEY, 'true');
        setShouldShowTour(false);
    };

    return { shouldShowTour, isLoading, resetTour, completeTour };
};

export default OnboardingTour;
